import { type HTMLAttributes } from 'react';
import { CheckStatusIcon, CrossStatusIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type VerificationTimelineState = 'done' | 'pending' | 'failed' | 'current';

export interface VerificationTimelineItem {
  readonly id: string;
  readonly label: string;
  readonly timestamp?: string;
  readonly state: VerificationTimelineState;
}

export interface VerificationTimelineProps extends HTMLAttributes<HTMLOListElement> {
  readonly items: ReadonlyArray<VerificationTimelineItem>;
}

const STATE_STYLES: Record<
  VerificationTimelineState,
  { ring: string; fill: string; text: string; pulse: boolean }
> = {
  done: { ring: 'border-success', fill: 'bg-success', text: 'text-ink', pulse: false },
  current: { ring: 'border-accent', fill: 'bg-accent', text: 'text-ink', pulse: true },
  pending: {
    ring: 'border-hairline-2',
    fill: 'bg-surface-2',
    text: 'text-ink-3',
    pulse: false,
  },
  failed: { ring: 'border-danger', fill: 'bg-danger', text: 'text-ink', pulse: false },
};

/**
 * Vertical step list — used by F09 to surface the verification lifecycle
 * (submitted → in review → approved/rejected) on both viewer and admin
 * surfaces. Pending items pulse gently, done show a check, failed show an X.
 */
export function VerificationTimeline({ items, className, ...rest }: VerificationTimelineProps) {
  return (
    <ol className={cn('relative flex flex-col', className)} {...rest}>
      {items.map((item, i) => {
        const sty = STATE_STYLES[item.state];
        const isLast = i === items.length - 1;
        return (
          <li key={item.id} className="relative ps-10 pb-5 last:pb-0">
            {!isLast ? (
              <span
                aria-hidden="true"
                className="absolute start-[11px] top-6 bottom-0 w-px bg-hairline-2"
              />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'absolute start-0 top-0.5 size-6 rounded-full border-2 inline-flex items-center justify-center',
                sty.ring,
                sty.fill,
                sty.pulse ? 'animate-pulse' : undefined,
              )}
            >
              {item.state === 'done' ? (
                <CheckStatusIcon size={12} color="currentColor" />
              ) : item.state === 'failed' ? (
                <CrossStatusIcon size={12} color="currentColor" />
              ) : null}
            </span>
            <div className="flex items-start justify-between gap-3 min-w-0">
              <span className={cn('text-[13px] font-semibold leading-tight', sty.text)}>
                {item.label}
              </span>
              {item.timestamp ? (
                <span className="text-[11px] font-mono tabular-nums text-ink-3 whitespace-nowrap">
                  {item.timestamp}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default VerificationTimeline;
